import React, { Component, ErrorInfo, ReactNode } from "react";
import { analytics } from "../../utils/analytics";
import ErrorHandlingModal from "./ErrorHandlingModal";

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    console.log("getDerivedStateFromError", error);

    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    const payload = {
      errorSeverity: "HIGH",
      errorSource: "INTERACTIVE",
      errorType: error.name,
      errorMessage: error.message,
      stackTrace: error.stack,
    } as Record<string, any>;

    if (errorInfo.componentStack) {
      payload.componentStack = errorInfo.componentStack;
    }

    if (errorInfo.digest) {
      payload.digest = errorInfo.digest;
    }

    analytics.trackError(error, payload);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  public handleReport = async () => {
    try {
      const userData = analytics.getUserData();
      const deviceInfo = analytics.getDeviceInfo();

      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/SendErrorTraceEmail`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subject: 'Interactive Element Error',
            info: [
              'A user has encountered an error in an Interactive Element. Please investigate and fix the issue.',
              'Date and Time:',
              new Date().toISOString(),
              'Device Info:',
              JSON.stringify(deviceInfo),
              'User Data:',
              JSON.stringify(userData),
              'Error:',
              this.state.error?.message,
              'Stack:',
              this.state.error?.stack,
            ],
          }),
        },
      );
      
      console.log("Error report sent successfully");
    } catch (error) {
      console.error('Error reporting error:', error);
    }
  };

  public resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      // Show the fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise show the error modal
      return (
        <div style={{ 
          width: '100%', 
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          padding: '20px'
        }}>
          <ErrorHandlingModal 
            error={this.state.error || undefined} 
            onClose={this.resetError}
            onReportIssue={this.handleReport}
          />
        </div>
      );
    }

    return <>{this.props.children}</>;
  }
}

export default ErrorBoundary;
